import moment from 'moment-timezone';
import { momentDaysOfWeek } from './constants';
import { isEmpty, isNil, onlyUnique } from './helpers';

export const isRestaurantClosedForDateWithTZ = (date, sessions, closures, areas) => {
  let isClosed = false;

  const openDays = sessions.map((session) => session.day).filter(onlyUnique);
  const openDates = sessions.map((session) => session.date).filter(onlyUnique);

  if (
    !openDays.includes(date.format('ddd').toLowerCase()) &&
    !openDates.includes(date.format('YYYY-MM-DD'))
  ) {
    isClosed = true;
  }

  const isSameDay = (date1, dayCompare) =>
    dayCompare !== null && momentDaysOfWeek[date1.day()] === dayCompare;

  const isSameDate = (date1, dateCompare) =>
    dateCompare !== null && date1.isSame(moment(dateCompare), 'day');

  // Whether the given date is the same date (e.g. 01/01/2024) or the same day (e.g. wednesday)
  const isSameDayOrDate = (date1, dateCompare, dayCompare) =>
    (dateCompare !== null && date1.isSame(moment(dateCompare), 'day')) ||
    (dayCompare !== null && momentDaysOfWeek[date1.day()] === dayCompare);

  // Whether the area has any normal sessions
  const areaHasSessionsForDay = (area) => {
    const sessionsForAreaForDay = area.sessions.filter(
      (session) =>
        (session.startTime !== 0 || session.endTime !== 0) &&
        isSameDayOrDate(date, session.date, session.day),
    );

    return !isEmpty(sessionsForAreaForDay);
  };

  // If an area has a single "closed event" (date with start and end times 0), then the area is closed for the day
  const areaHasCloseEvent = (area) => {
    return area.sessions.some((session) => {
      return (
        date.isSame(moment(session.date), 'day') && session.startTime === 0 && session.endTime === 0
      );
    });
  };

  const hasDefaultCloseDate = sessions.some(
    (session) =>
      session.startTime === 0 &&
      session.endTime === 0 &&
      (session.areaId === null || session.areaId <= 0) &&
      isSameDate(date, session.date),
  );

  const hasDefaultCloseDay = sessions.some(
    (session) =>
      session.startTime === 0 &&
      session.endTime === 0 &&
      (session.areaId === null || session.areaId <= 0) &&
      isSameDay(date, session.day),
  );

  // Does the venue have any "default" (non-area-specific) close events?
  const hasDefaultCloseEvent = hasDefaultCloseDate || hasDefaultCloseDay;

  // If every area has an event that counts as a closure, then the venue is considered closed for the day
  // Note: double negative. If any area is NOT closed, then the restaurant is open
  // Also if the area just has no sessions in general, then it's closed
  const allAreasClosed = !areas.some(
    (area) => areaHasSessionsForDay(area) && !areaHasCloseEvent(area),
  );

  // If there is a default closure event for the date or day
  if (hasDefaultCloseEvent) {
    // If the closure is for the day of the week, check if there are open sessions for this date
    if (hasDefaultCloseDay) {
      const hasOpenSessionForDate = sessions.some(
        (session) =>
          (session.startTime !== 0 || session.endTime !== 0) &&
          isSameDayOrDate(date, session.date, session.day),
      );

      // If there is an open session, the restaurant is open, otherwise it's closed
      isClosed = !hasOpenSessionForDate;
    }

    // Check if there is a specific closure for this date
    if (hasDefaultCloseDate) {
      isClosed = true;
    }
  }

  // If all areas are closed, then they're closed
  if (allAreasClosed) {
    isClosed = true;
  }

  if (isNil(closures)) {
    return isClosed;
  }

  const hasClosure = closures.some((closure) =>
    date.isBetween(moment(closure.startDate), moment(closure.endDate), 'day', '[]'),
  );

  if (hasClosure) {
    isClosed = true;
  }

  return isClosed;
};
